import React, { useEffect, useMemo, useState } from 'react'
import { useDocumentTitle } from '@mantine/hooks'
import { useSearchParams } from 'react-router-dom'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { CountryCodes } from 'types/types'
import { AgreementsForm } from './components/AgreementsForm'
import { PageWrapper } from './components/PageWrapper'
import { SessionStatusView } from './components/SessionStatusView'
import {
  EmbeddedAgreementStatuses,
  EmbeddedUseCases,
  PARAMS,
  SessionStatusCodes,
  SessionStatusMapping,
  VARIANT_ACCOUNT_REFERENCE,
} from './helpers/constants'
import { EmbeddedAgreementStatus, SessionStatusCode, UIConfig } from './helpers/types'
import { isValidHexCode, toBoolean } from './helpers/utils'
import { useCustomerPendingAgreementsQuery } from './requests/useCustomerPendingAgreementsQuery'
import { DakotaFooter } from './variations/DakotaFooter'

export const CustomerPendingAgreements = () => {
  useDocumentTitle(`${process.env.SITE_TITLE} | Agreements`)
  const [searchParams] = useSearchParams()
  const [sessionStatus, setSessionStatus] = useState<EmbeddedAgreementStatus>()

  const reference = searchParams?.get(PARAMS.REFERENCE) ?? ''
  const redirectUri = searchParams?.get(PARAMS.REDIRECT) ?? ''

  const uiConfig = useMemo<UIConfig>(() => {
    return {
      primaryColor: isValidHexCode(searchParams?.get(PARAMS.PRIMARY_COLOR) ?? ''),
      isDarkMode: toBoolean(searchParams?.get(PARAMS.IS_DARK_MODE), false),
      isBranded: toBoolean(searchParams?.get(PARAMS.IS_BRANDED), true),
    }
  }, [searchParams])

  const {
    data: { agreements = [], status, countryCode, accountReference, useCase } = {},
    isFetching,
    isError,
  } = useCustomerPendingAgreementsQuery(reference)

  const isDakota = accountReference === VARIANT_ACCOUNT_REFERENCE?.dakota

  const pendingAgreements = (agreements || [])?.filter(
    ({ status }) => status === EmbeddedAgreementStatuses.PENDING
  )

  const isUsEntity = countryCode === CountryCodes.US

  const mappedSessionStatus: SessionStatusCode | undefined = useMemo(() => {
    if (sessionStatus && sessionStatus in SessionStatusMapping) {
      return SessionStatusMapping[sessionStatus as keyof typeof SessionStatusMapping]
    }
    return undefined
  }, [sessionStatus])

  const showHeaderAndFooter = useMemo(
    () => !isFetching && !isError && !!reference,
    [isFetching, isError, reference]
  )

  const onSuccess = () => {
    setSessionStatus(EmbeddedAgreementStatuses.SIGNED)
  }

  useEffect(() => {
    if (!isFetching && status) {
      setSessionStatus(status)
    }
  }, [isFetching, status])

  const StatusView = useMemo(() => {
    if (!reference) {
      return <SessionStatusView statusCode={SessionStatusCodes.NO_REFERENCE} uiConfig={uiConfig} />
    }
    if (isFetching) {
      return (
        <Box>
          <RowsSkeletonLoader rows={3} />
        </Box>
      )
    }
    if (isError) {
      return <SessionStatusView statusCode={SessionStatusCodes.QUERY_ERROR} uiConfig={uiConfig} />
    }
    if (mappedSessionStatus) {
      return <SessionStatusView statusCode={mappedSessionStatus} uiConfig={uiConfig} />
    }
    if (!pendingAgreements?.length) {
      return <SessionStatusView statusCode={SessionStatusCodes.NO_AGREEMENTS} uiConfig={uiConfig} />
    }
  }, [reference, uiConfig, isFetching, isError, mappedSessionStatus, pendingAgreements?.length])

  return (
    <PageWrapper
      showHeaderAndFooter={showHeaderAndFooter}
      uiConfig={uiConfig}
      isUsEntity={isUsEntity}
      useCase={isDakota ? EmbeddedUseCases.BOTH : useCase}
      {...(isDakota && { Footer: <DakotaFooter /> })}
    >
      {StatusView || (
        <AgreementsForm
          reference={reference}
          isDakota={isDakota}
          redirectUri={redirectUri}
          isUsEntity={isUsEntity}
          agreements={pendingAgreements}
          uiConfig={uiConfig}
          onSuccess={onSuccess}
        />
      )}
    </PageWrapper>
  )
}
